/**
 * Day 101: rank what you will drive (the new, accelerate and fix priorities)
 * and record the top three to five as a decision. The ranking is a personal
 * Stack Ranking session (personalRanking.ts); the decision is on Mandate's
 * record and mirrored to the shared decisions record.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useMandateStore } from '@/stores/mandate/mandateStore';
import type { Item } from '@/types/prioritization/lifecycle';
import { Select } from '@/components/ds';
import { getAppDefinition } from '../registry';
import { Button } from '@/components/ui/Button';
import { AppShell, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton, SecondaryButton } from '../components/primitives';
import { rankAlone } from '../personalRanking';
import {
  DISPOSITIONS,
  FIRST_100_DAYS_APP_ID,
  day101Candidates,
  day101Decision,
  dispositionOf,
  latestDay101Decision,
} from './first100Days';
import { useJourney } from './useJourney';
import NoMandate from './NoMandate';

const TOP_CHOICES = [3, 4, 5];

export default function Day101Screen() {
  const app = getAppDefinition(FIRST_100_DAYS_APP_ID)!;
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const items = useItemsStore((s) => s.items);
  const { loaded, transition, decisions } = useJourney();

  const candidates = useMemo(() => day101Candidates(items), [items]);
  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const [order, setOrder] = useState<string[]>([]);
  const [topCount, setTopCount] = useState(3);
  const [again, setAgain] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOrder((current) => {
      const ids = candidates.map((item) => item.id);
      const still = current.filter((id) => ids.includes(id));
      return [...still, ...ids.filter((id) => !still.includes(id))];
    });
  }, [candidates]);

  const back = { backTo: '/apps/first-100-days', backLabel: 'Back to the journey' };
  if (!loaded) {
    return (
      <AppShell app={app} {...back}>
        <p className="font-body text-sm text-[var(--text-ter)]">Reading your mandate…</p>
      </AppShell>
    );
  }
  if (!transition) {
    return (
      <AppShell app={app} {...back}>
        <NoMandate />
      </AppShell>
    );
  }

  const recorded = latestDay101Decision(decisions);
  const shown = Math.min(topCount, order.length);
  const label = (item: Item) => DISPOSITIONS.find((d) => d.id === dispositionOf(item))?.label ?? '';

  const move = (index: number, by: number) => {
    setOrder((current) => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const rankAndRecord = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    const ranked = await rankAlone({
      appId: FIRST_100_DAYS_APP_ID,
      name: 'Day 101: my first priorities',
      order,
      userId,
      userName: userName || 'You',
      originModule: 'mandate',
    });
    if (!ranked.ok) {
      setBusy(false);
      setError(ranked.error);
      return;
    }
    const top = order.slice(0, shown).map((id) => byId.get(id)).filter((i): i is Item => Boolean(i));
    const store = useMandateStore.getState();
    const decision = await store.addDecision(day101Decision(top, ranked.sessionId));
    if (decision) await store.mirrorDecisionToPlatform(decision.id);
    setBusy(false);
    if (!decision) {
      setError('Your ranking is saved, but the decision could not be recorded. Try again.');
      return;
    }
    setAgain(false);
  };

  if (recorded && !again) {
    return (
      <AppShell app={app} {...back}>
        <ScreenHeading title="Day 101" subtitle="Your first priorities, ranked and recorded." />
        <AppCard className="mt-4">
          <Eyebrow>Recorded {new Date(recorded.createdAt).toLocaleDateString()}</Eyebrow>
          <ol className="mt-2 space-y-1.5">
            {(recorded.description ?? '').split('\n').filter(Boolean).map((line) => (
              <li key={line} className="font-heading font-semibold text-[var(--text-pri)]">
                {line}
              </li>
            ))}
          </ol>
          <p className="mt-3 font-body text-sm text-[var(--text-sec)]">{recorded.rationale}</p>
        </AppCard>
        <div className="mt-4 flex flex-wrap gap-2">
          <SecondaryButton onClick={() => navigate('/apps/mandate/decisions')}>See it in your decision record</SecondaryButton>
          <SecondaryButton onClick={() => setAgain(true)}>Rank again</SecondaryButton>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell app={app} {...back}>
      <ScreenHeading title="Day 101" subtitle="You know your priorities. Rank what you will drive, and record the first ones." />

      {candidates.length < 2 ? (
        <div className="mt-4">
          <EmptyState
            title="Sort your priorities first"
            hint="Day 101 ranks the ones you marked new, accelerate or fix. You need at least two."
            action={<PrimaryButton onClick={() => navigate('/apps/first-100-days/priorities')}>Go to priority discovery</PrimaryButton>}
          />
        </div>
      ) : (
        <>
          <p className="mt-4 font-body text-sm text-[var(--text-sec)]">Most important first.</p>
          <ol className="mt-2 space-y-2">
            {order.map((id, index) => {
              const item = byId.get(id);
              if (!item) return null;
              return (
                <li
                  key={id}
                  className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 ${
                    index < shown
                      ? 'border-primary-300 bg-white dark:border-primary-700 dark:bg-[var(--surface)]'
                      : 'border-gray-200 bg-white dark:border-[var(--rule)] dark:bg-[var(--surface)]'
                  }`}
                >
                  <span className="w-5 font-mono text-xs text-[var(--text-ter)]">{index + 1}</span>
                  <span className="min-w-0 flex-1 font-body text-sm text-[var(--text-pri)]">{item.title}</span>
                  <span className="font-mono text-[10px] uppercase text-[var(--text-ter)]">{label(item)}</span>
                  <Button variant="ghost" size="sm" aria-label="Move up" onClick={() => move(index, -1)} disabled={index === 0} className="text-[var(--text-ter)] disabled:opacity-30">
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" aria-label="Move down" onClick={() => move(index, 1)} disabled={index === order.length - 1} className="text-[var(--text-ter)] disabled:opacity-30">
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </li>
              );
            })}
          </ol>
          <label className="mt-4 flex items-center gap-2 font-body text-sm text-[var(--text-sec)]">
            Record the top
            <Select
              size="sm"
              className="!w-auto"
              value={topCount}
              onChange={(e) => setTopCount(Number(e.target.value))}
              aria-label="How many to record"
            >
              {TOP_CHOICES.filter((n) => n <= Math.max(order.length, 3)).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
            as my first priorities
          </label>
          {error ? <p role="alert" className="mt-2 font-body text-sm text-[var(--coral)]">{error}</p> : null}
          <PrimaryButton className="mt-4" onClick={() => void rankAndRecord()} disabled={busy || order.length < 2}>
            {busy ? 'Ranking and recording…' : 'Rank and record'}
          </PrimaryButton>
        </>
      )}
    </AppShell>
  );
}
